// import { Add } from "@mui/icons-material";
// import { styled, Typography, useTheme } from "@mui/material";
// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { Button, FlexRow, Spacer } from "../../components";
// import { ColorsCodeType } from "../../typings";

// const Wrapper = styled(FlexRow)`
//   width: 100%;
//   flex-wrap: nowrap;
//   align-items: center;
//   justify-content: space-between;
//   padding: 10px 0px 15px 0px;
// `;

// export const StyledPrimaryButton = styled(Button)`
//   min-width: 0;
//   min-height: 32px;
//   padding: 0px 10px;
//   border-radius: 5px;
//   color: ${({ theme }) => theme.palette.colors.gray["900"]};
// `;

// const ScholarshipApplicationScreenHeader = () => {
//   const theme = useTheme();
//   const {
//     palette: { colors },
//   } = theme;

//   const navigate = useNavigate();
//   const handleCreateScholarshipApplication = () =>
//     navigate("/scholarship-form");

//   return (
//     <Wrapper>
//       <Typography
//         variant="h4"
//         gutterBottom
//         component="div"
//         sx={{ fontWeight: "bold", color: "primary.main" }}
//       >
//         Scholarship Applications
//       </Typography>
//       {/* add search bar here */}
//       <StyledPrimaryButton
//         buttonColor={colors["yellow"]["300"] as ColorsCodeType}
//         onClick={handleCreateScholarshipApplication}
//       >
//         <Add fontSize="small" />
//         <Spacer horizontal={0.3} />
//         {"Create Application"}
//       </StyledPrimaryButton>
//     </Wrapper>
//   );
// };

// export default ScholarshipApplicationScreenHeader;


"use client"

import { Add } from "@mui/icons-material"
import { styled, Typography, useTheme } from "@mui/material"
import React, { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { MdRedo } from "react-icons/md"
import { Button, FlexRow, Spacer } from "../../components"
import { UserRolesEnum, type ColorsCodeType } from "../../typings"
import SearchBar from "../../components/atoms/SearchBar/SearchBar"
import api from "../../api/axiosInstance"
import { AuthContext } from "../../context/AuthContext"
import { STUDENT_ONLY_PERMISSIONS } from "../../data/roles"

const Wrapper = styled(FlexRow)`
  width: 100%;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0px 15px 0px;
  gap: 16px;
`

const LeftSection = styled("div")`
  display: flex;
  align-items: center;
  flex: 1;
`

const RightSection = styled("div")`
  display: flex;
  align-items: center;
  gap: 16px;
`

export const StyledPrimaryButton = styled(Button)`
  min-width: 0;
  min-height: 32px;
  padding: 0px 10px;
  border-radius: 5px;
  color: ${({ theme }) => theme.palette.colors.gray["900"]};
  white-space: nowrap;
`

interface ScholarshipApplicationScreenHeaderProps {
  onSearch: (searchTerm: string) => void
  /**
   * Number of applications already on file for the signed-in user.
   * `null` = not yet loaded. Used to switch the header's primary action
   * for students, who are limited to one application profile and must
   * Reapply against their root application each semester so the full
   * scholarship-history trail lives under one root.
   */
  totalApplicationCount: number | null
  /** Id of the student's root (parent-less) application, used to target Reapply. */
  rootApplicationId: number | null
}

const ScholarshipApplicationScreenHeader: React.FC<ScholarshipApplicationScreenHeaderProps> = ({ onSearch, totalApplicationCount, rootApplicationId }) => {
  const theme = useTheme()
  const {
    palette: { colors },
  } = theme

  const navigate = useNavigate()
  const auth = useContext(AuthContext)
  const isStudent = [...STUDENT_ONLY_PERMISSIONS].includes(auth?.role as UserRolesEnum)
  const studentAlreadyHasApplication =
    isStudent && totalApplicationCount !== null && totalApplicationCount > 0
  const showReapplyButton = studentAlreadyHasApplication && rootApplicationId !== null
  const [hasDraft, setHasDraft] = useState(false)
  const [checkingDraft, setCheckingDraft] = useState(true)

  useEffect(() => {
    const checkForDraft = async () => {
      try {
        const response = await api.get("/user-temporary-bucket")
        
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          const tempBucket = response.data.data.find(
            (item: any) => item.record_type === "UNSUBMITTED_SCHOLARSHIP_APPLICATION"
          )
          
          if (tempBucket && tempBucket.record) {
            try {
              let parsedRecord
              
              if (typeof tempBucket.record === 'string') {
                try {
                  // Try direct JSON parse first
                  parsedRecord = JSON.parse(tempBucket.record)
                } catch {
                  // Parse Python dict format
                  const jsCode = tempBucket.record
                    .replace(/False/g, 'false')
                    .replace(/True/g, 'true')
                    .replace(/None/g, 'null')
                  // eslint-disable-next-line no-eval
                  parsedRecord = eval('(' + jsCode + ')')
                }
              } else {
                parsedRecord = tempBucket.record
              }
              
              if (parsedRecord && parsedRecord.scholarship_application_form) {
                setHasDraft(true)
              }
            } catch (parseError) {
              console.error("Error parsing draft record:", parseError)
            }
          }
        }
      } catch (error) {
        console.error("Error checking for draft:", error)
      } finally {
        setCheckingDraft(false)
      }
    }

    checkForDraft()
  }, [])

  const handleCreateScholarshipApplication = () => {
    // Open in new tab
    window.open("/scholarship-form", "_blank")
  }

  const handleReapplyFromRoot = () => {
    if (rootApplicationId === null) return
    window.open(`/scholarship-reapply/${rootApplicationId}`, "_blank", "noopener,noreferrer")
  }

  return (
    <Wrapper>
      <LeftSection>
        <Typography
          variant="h4"
          gutterBottom
          component="div"
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            marginBottom: 0,
            marginRight: 2,
          }}
        >
          Scholarship Applications
        </Typography>
      </LeftSection>

      <RightSection>
        <SearchBar onSearch={onSearch} placeholder="Search applications..." />

        {showReapplyButton ? (
          <StyledPrimaryButton
            buttonColor={colors["yellow"]["300"] as ColorsCodeType}
            onClick={handleReapplyFromRoot}
          >
            <MdRedo style={{ height: "16px", width: "16px" }} />
            <Spacer horizontal={0.3} />
            Reapply
          </StyledPrimaryButton>
        ) : (
          !studentAlreadyHasApplication && (
            <StyledPrimaryButton
              buttonColor={colors["yellow"]["300"] as ColorsCodeType}
              onClick={handleCreateScholarshipApplication}
              disabled={checkingDraft}
            >
              <Add fontSize="small" />
              <Spacer horizontal={0.3} />
              {checkingDraft ? "Loading..." : hasDraft ? "Resume Application" : "Create New Application"}
            </StyledPrimaryButton>
          )
        )}
      </RightSection>
    </Wrapper>
  )
}

export default ScholarshipApplicationScreenHeader
